import type { AISettings } from '../types';

/**
 * Local AI Engine
 * Provides deterministic AI-like responses without external API calls.
 * Intent detection, sentiment analysis, lead scoring, language detection,
 * tone control, context-aware replies, and order tracking.
 */

export interface AIResponse {
  reply: string;
  intent: string;
  confidence: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  sentimentScore: number; // -1 to 1
  language: string;
  shouldEscalate: boolean;
  escalationReason?: string;
  leadScore: number; // 0-100
  leadCategory: 'cold' | 'warm' | 'hot';
  suggestedReplies: string[];
  detectedTone: string;
  orderRelated: boolean;
}

export type AITone = 'friendly' | 'sales' | 'urgent' | 'formal';

// ===================== INTENT MAP =====================

const INTENT_MAP: { keywords: string[]; intent: string; confidence: number }[] = [
  { keywords: ['price', 'cost', 'how much', 'expensive', 'cheap', 'discount', 'offer', 'promotion', 'deal'], intent: 'pricing_inquiry', confidence: 0.92 },
  { keywords: ['order', 'track', 'shipping', 'delivery', 'arrive', 'shipped', 'where is', 'my order', 'tracking'], intent: 'order_tracking', confidence: 0.95 },
  { keywords: ['return', 'refund', 'exchange', 'broken', 'damaged', 'wrong', 'defect', 'warranty'], intent: 'return_request', confidence: 0.93 },
  { keywords: ['buy', 'purchase', 'want', 'interested', 'add to cart', 'checkout', 'order now'], intent: 'purchase_intent', confidence: 0.90 },
  { keywords: ['hello', 'hi', 'hey', 'good morning', 'good evening', 'salaam', 'bonjour', 'مرحبا', 'السلام'], intent: 'greeting', confidence: 0.98 },
  { keywords: ['thank', 'thanks', 'great', 'awesome', 'love', 'excellent', 'perfect', 'شكرا'], intent: 'positive_feedback', confidence: 0.96 },
  { keywords: ['help', 'support', 'problem', 'issue', 'complaint', 'bad', 'terrible', 'مشكل'], intent: 'support_request', confidence: 0.91 },
  { keywords: ['cancel', 'stop', 'unsubscribe', 'remove', "don't want", 'الغاء'], intent: 'cancellation', confidence: 0.94 },
  { keywords: ['stock', 'available', 'inventory', 'in stock', 'color', 'size', 'model'], intent: 'product_inquiry', confidence: 0.89 },
  { keywords: ['pay', 'payment', 'cod', 'cash', 'card', 'visa', 'bank', 'transfer'], intent: 'payment_inquiry', confidence: 0.91 },
  { keywords: ['agent', 'human', 'person', 'real person', 'speak to someone', 'manager'], intent: 'escalation_request', confidence: 0.97 },
  { keywords: ['confirm', 'yes', 'ok', 'sure', 'agree', 'proceed', 'نعم', 'واخا'], intent: 'confirmation', confidence: 0.88 },
  { keywords: ['no', 'nope', 'not interested', 'never', 'لا'], intent: 'rejection', confidence: 0.87 },
];

// ===================== TONE-AWARE REPLY MAP =====================

const REPLY_MAP: Record<string, Record<AITone, string[]>> = {
  greeting: {
    friendly: ['Hello! 😊 Welcome to our store. How can I help you today?', 'Hi there! Thanks for reaching out. What can I do for you?'],
    sales: ['Welcome! 🎉 We have amazing deals waiting for you. What are you looking for?', 'Hi! Great timing — we just launched new products. Interested?'],
    urgent: ['Hello! I\'m here to help right away. What do you need?', 'Hi! Let me assist you immediately. What\'s your question?'],
    formal: ['Good day. Welcome to our store. How may I assist you?', 'Hello. Thank you for contacting us. How can we be of service?'],
  },
  pricing_inquiry: {
    friendly: ['Happy to help with pricing! 💰 Which product caught your eye?', 'Great question! Let me find the best price for you. What item?'],
    sales: ['Excellent question! We have the best prices guaranteed. Plus free shipping on orders over 500 MAD! Which product?', 'You\'re in luck! We\'re running a special promotion. What item are you interested in?'],
    urgent: ['Let me get you that pricing info right away. Which product?', 'I\'ll check the price immediately. What product do you need?'],
    formal: ['I\'d be happy to provide pricing information. Could you specify the product?', 'Certainly. Please let me know which product you\'re inquiring about.'],
  },
  order_tracking: {
    friendly: ['I can help track your order! 📦 Could you share your order number?', 'Sure! Let me check on that delivery for you. What\'s your order reference?'],
    sales: ['I\'ll check your order right away! While we\'re at it, we have new arrivals you might love. Order number?', 'Let me track that for you. Also, check our new collection! What\'s the order number?'],
    urgent: ['I\'m checking your order status now. Please provide the order number.', 'Let me locate your order immediately. Reference number?'],
    formal: ['I shall look into your order status. Please provide your order reference number.', 'Certainly. I will check the status of your order. May I have the order number?'],
  },
  return_request: {
    friendly: ['I\'m sorry about that! 😔 We\'ll make it right. Can you describe what happened?', 'Oh no! Let me help you with the return process. What\'s the issue?'],
    sales: ['I\'m sorry to hear that. We want you to be 100% happy. Let me process this and also show you alternatives.', 'We\'ll get this sorted! Would you like a replacement or a different product?'],
    urgent: ['I understand the urgency. Let me start the return process now. What\'s wrong?', 'I\'ll process this return immediately. Please describe the issue.'],
    formal: ['I apologize for the inconvenience. Please describe the issue so we can arrange a return.', 'We sincerely apologize. I will initiate the return process. Please provide details.'],
  },
  purchase_intent: {
    friendly: ['Great choice! 🛍️ I can help you place the order. Ready to proceed?', 'Awesome! That\'s one of our best sellers. Want me to help with checkout?'],
    sales: ['Fantastic choice! 🔥 Order now and get FREE express shipping! Ready?', 'Smart decision! This is selling fast. Let me secure one for you before it\'s gone!'],
    urgent: ['Let me process your order right away. Confirm the details?', 'I\'ll get your order placed immediately. Shall we proceed?'],
    formal: ['Excellent selection. I can assist you with the ordering process. Shall we proceed?', 'A fine choice. I will guide you through the checkout process.'],
  },
  positive_feedback: {
    friendly: ['Thank you so much! 🎉 That means the world to us! Anything else I can help with?', 'You made our day! ❤️ We\'re so glad you\'re happy!'],
    sales: ['Thank you! We\'d love for you to try our new collection too. Want a sneak peek?', 'So glad you love it! Have you seen our latest arrivals? I think you\'d love them!'],
    urgent: ['Thank you! Is there anything else you need?', 'Appreciated! Anything else I can do for you?'],
    formal: ['Thank you for your kind feedback. We value your patronage. Is there anything else we can assist with?', 'We appreciate your positive words. Please don\'t hesitate to contact us again.'],
  },
  support_request: {
    friendly: ['I\'m here to help! 🙋 Tell me what\'s going on and I\'ll sort it out.', 'Sorry you\'re having trouble. Let me help fix this right away!'],
    sales: ['I\'m sorry about the issue. Let me solve this and maybe offer you a special discount for the trouble.', 'We\'ll fix this! And as a goodwill gesture, I\'d like to offer you something special.'],
    urgent: ['I understand this is urgent. Let me resolve this now. What\'s the issue?', 'I\'m on it. Please describe the problem so I can fix it immediately.'],
    formal: ['I apologize for any inconvenience. Please describe the issue in detail so we can resolve it.', 'We regret the difficulty you\'re experiencing. Please provide specifics so we may assist.'],
  },
  cancellation: {
    friendly: ['I understand. Before I process this, is there anything we can do to help? 🤔', 'No worries! Can I ask what changed your mind? Maybe I can help.'],
    sales: ['Before cancelling, would you consider a 15% discount to keep your order?', 'I understand. What if I could offer you free shipping or a discount instead?'],
    urgent: ['I\'ll process the cancellation now. Please confirm the order details.', 'Processing cancellation. Order reference?'],
    formal: ['I understand your decision. May I inquire about the reason for cancellation before processing?', 'Certainly. I shall process the cancellation. Could you provide the order details?'],
  },
  product_inquiry: {
    friendly: ['Let me check that for you! 🔍 What product and size/color?', 'Great question! I\'ll look it up right away. What are you looking for?'],
    sales: ['Great taste! That\'s one of our trending items. Let me check stock for you!', 'Excellent choice! Very popular right now. Let me confirm availability.'],
    urgent: ['Checking stock now. Which product, size, and color?', 'I\'ll verify availability immediately. Specifics?'],
    formal: ['I will check our current inventory. Please specify the product details.', 'Certainly. I shall verify stock availability. Which item are you inquiring about?'],
  },
  payment_inquiry: {
    friendly: ['We accept COD, cards, and bank transfers! 💳 Which works best for you?', 'We have flexible payment options! COD is our most popular. What do you prefer?'],
    sales: ['Great news — we offer COD with no extra fees! Plus card payments with instant confirmation. Ready to order?', 'You can pay however you like! COD, card, or bank transfer. All secure. Want to proceed?'],
    urgent: ['We accept COD, card, and transfer. Which method?', 'Payment methods: COD, Credit Card, Bank Transfer. Your choice?'],
    formal: ['We accept Cash on Delivery, credit/debit cards, and bank transfers. Which method would you prefer?', 'Our payment options include COD, card payments, and wire transfers.'],
  },
  escalation_request: {
    friendly: ['Of course! Let me connect you with our team right away. One moment please! 🙏'],
    sales: ['Absolutely! A specialist is ready to help you. Connecting now...'],
    urgent: ['Connecting you to a human agent now.'],
    formal: ['Certainly. I will transfer you to a human representative immediately.'],
  },
  confirmation: {
    friendly: ['Perfect! ✅ Let me go ahead and process that for you!', 'Great, confirmed! I\'ll take care of it right away.'],
    sales: ['Awesome! Processing now. You won\'t regret this! 🎉', 'Confirmed! Great decision. Processing...'],
    urgent: ['Confirmed. Processing now.', 'Got it. Moving forward.'],
    formal: ['Understood. I shall proceed accordingly.', 'Confirmed. Processing your request now.'],
  },
  rejection: {
    friendly: ['No problem at all! If you change your mind, we\'re always here. 😊', 'That\'s totally fine! Feel free to reach out anytime.'],
    sales: ['I understand! Would you like me to let you know about future deals instead?', 'No worries! Can I keep you updated on new arrivals and special offers?'],
    urgent: ['Understood. Anything else?', 'Noted. Let me know if you need anything else.'],
    formal: ['Understood. Please don\'t hesitate to contact us if you change your mind.', 'Very well. We remain at your service should you reconsider.'],
  },
  unknown: {
    friendly: ['Thanks for your message! Could you tell me a bit more about what you need? 🤔', 'I want to make sure I help you right. Could you elaborate?'],
    sales: ['Thanks for reaching out! Are you interested in any of our products? I can help!', 'I\'d love to help! Are you looking for something specific from our store?'],
    urgent: ['Could you provide more details so I can assist you?', 'Please clarify your request so I can help immediately.'],
    formal: ['Thank you for your message. Could you provide additional details about your inquiry?', 'I appreciate your message. Please elaborate so I may assist you properly.'],
  },
};

// ===================== ARABIC/DARIJA REPLY MAP =====================

const ARABIC_REPLY_MAP: Record<string, string[]> = {
  greeting: ['مرحبا! 😊 أهلا بيك فالمتجر ديالنا. كيفاش نقدر نعاونك؟', 'السلام عليكم! شنو كتقلب عليه اليوم؟'],
  pricing_inquiry: ['بغيتي تعرف الثمن؟ 💰 قولي شنو المنتج وغادي نعطيك أحسن سعر!', 'عندنا أثمنة مزيانة! شنو بغيتي تشري؟'],
  order_tracking: ['غادي نشوف ليك الطلبية ديالك 📦 عطيني الرقم ديالها', 'نشوف ليك فين وصلات. شنو رقم الطلبية؟'],
  purchase_intent: ['اختيار مزيان! 🛍️ بغيتي نعاونك تكمل الشراء؟', 'هادشي كيتباع بزاف! بغيتي نحجز ليك واحد؟'],
  support_request: ['سمحلي على المشكل! 🙏 قولي شنو وقع وغادي نحلو', 'مكاين مشكل، غادي نعاونك. شنو المشكلة؟'],
  unknown: ['شكرا على الرسالة! ممكن توضح ليا شوية شنو كتحتاج؟', 'بغيت نفهم مزيان. ممكن تشرح ليا أكثر؟'],
};

// ===================== CORE FUNCTIONS =====================

export function classifyIntent(message: string): { intent: string; confidence: number } {
  const lower = message.toLowerCase();
  let bestMatch = { intent: 'unknown', confidence: 0.45 };

  for (const entry of INTENT_MAP) {
    const matchCount = entry.keywords.filter(kw => lower.includes(kw)).length;
    if (matchCount > 0) {
      const adjustedConfidence = Math.min(0.99, entry.confidence * Math.min(1, 0.5 + matchCount * 0.25));
      if (adjustedConfidence > bestMatch.confidence) {
        bestMatch = { intent: entry.intent, confidence: adjustedConfidence };
      }
    }
  }

  return bestMatch;
}

export function detectSentiment(message: string): { sentiment: 'positive' | 'neutral' | 'negative'; score: number } {
  const lower = message.toLowerCase();
  const positiveWords = ['thank', 'great', 'love', 'awesome', 'excellent', 'happy', 'perfect', 'good', 'best', 'wonderful', 'amazing', 'beautiful', 'شكرا', 'مزيان', 'ممتاز'];
  const negativeWords = ['bad', 'terrible', 'hate', 'awful', 'worst', 'angry', 'frustrated', 'disappointed', 'problem', 'complaint', 'broken', 'scam', 'مشكل', 'خايب'];

  const posScore = positiveWords.filter(w => lower.includes(w)).length;
  const negScore = negativeWords.filter(w => lower.includes(w)).length;

  const total = posScore + negScore || 1;
  const score = (posScore - negScore) / total; // -1 to 1

  if (score > 0.2) return { sentiment: 'positive', score };
  if (score < -0.2) return { sentiment: 'negative', score };
  return { sentiment: 'neutral', score };
}

export function detectLanguage(message: string): string {
  if (/[\u0600-\u06FF]/.test(message)) {
    // Check for Darija-specific words
    if (/واخا|بغيت|كتقلب|شنو|فين|ديال|مزيان|خويا/.test(message)) return 'Darija';
    return 'Arabic';
  }
  if (/[àâçéèêëîïôûùüÿœæ]/i.test(message)) return 'French';
  if (/[áéíóúñ¿¡]/i.test(message)) return 'Spanish';
  return 'English';
}

export function calculateLeadScore(
  message: string,
  intent: string,
  sentiment: 'positive' | 'neutral' | 'negative',
  contactData?: { orderCount?: number; totalSpent?: number; tags?: string[]; leadStatus?: string; messageCount?: number }
): { score: number; category: 'cold' | 'warm' | 'hot' } {
  let score = 30; // baseline

  // Intent scoring
  const intentScores: Record<string, number> = {
    purchase_intent: 30, pricing_inquiry: 20, product_inquiry: 15, payment_inquiry: 25,
    confirmation: 25, order_tracking: 10, greeting: 5, positive_feedback: 10,
    support_request: -5, cancellation: -15, rejection: -10, return_request: -10,
    escalation_request: 0, unknown: 0,
  };
  score += intentScores[intent] || 0;

  // Sentiment boost
  if (sentiment === 'positive') score += 10;
  if (sentiment === 'negative') score -= 10;

  // Contact history
  if (contactData) {
    if ((contactData.orderCount || 0) > 0) score += 15;
    if ((contactData.totalSpent || 0) > 1000) score += 10;
    if ((contactData.messageCount || 0) > 5) score += 5;
    if (contactData.tags?.includes('vip')) score += 15;
    if (contactData.leadStatus === 'customer') score += 10;
  }

  score = Math.max(0, Math.min(100, score));

  const category: 'cold' | 'warm' | 'hot' = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
  return { score, category };
}

export function shouldEscalate(message: string, settings?: Partial<AISettings>): { escalate: boolean; reason?: string } {
  const { intent, confidence } = classifyIntent(message);
  const { sentiment } = detectSentiment(message);
  const threshold = settings?.confidenceThreshold || 0.7;

  if (intent === 'escalation_request') return { escalate: true, reason: 'Customer requested human agent' };
  if (confidence < threshold) return { escalate: true, reason: `Low confidence (${(confidence * 100).toFixed(0)}% < ${(threshold * 100).toFixed(0)}%)` };
  if (sentiment === 'negative' && intent === 'support_request') return { escalate: true, reason: 'Negative sentiment with support request' };
  if (intent === 'return_request') return { escalate: true, reason: 'Return/refund request requires human review' };

  return { escalate: false };
}

export function generateSuggestedReplies(intent: string, tone: AITone = 'friendly'): string[] {
  const replies = REPLY_MAP[intent]?.[tone] || REPLY_MAP.unknown[tone];
  // Return up to 3 suggestions
  const shuffled = [...replies].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(3, shuffled.length));
}

export function generateOrderTrackingReply(
  orderData: { orderStatus?: string; deliveryStatus?: string; productName?: string; orderAmount?: number },
  tone: AITone = 'friendly'
): string {
  const status = orderData.orderStatus || 'unknown';
  const delivery = orderData.deliveryStatus || 'unknown';
  const product = orderData.productName || 'your order';
  const amount = orderData.orderAmount ? `${orderData.orderAmount} MAD` : '';

  const statusMessages: Record<string, string> = {
    pending: `Your order for ${product}${amount ? ` (${amount})` : ''} is being processed. We'll update you once it ships! 📋`,
    confirmed: `Great news! Your order for ${product} has been confirmed and is being prepared for shipment. 🎉`,
    shipped: `${product} is on its way! 🚚 Delivery status: ${delivery}. You should receive it soon!`,
    delivered: `${product} has been delivered! ✅ We hope you enjoy it. Let us know if you need anything.`,
    cancelled: `Your order for ${product} has been cancelled. If this was a mistake, please let us know.`,
  };

  return statusMessages[status] || `Your order status is: ${status}. Delivery: ${delivery}. Let me know if you need more details.`;
}

// ===================== MAIN GENERATE FUNCTION =====================

export function generateReply(
  message: string,
  settings?: Partial<AISettings>,
  context?: {
    contactData?: any;
    orderData?: any;
    conversationHistory?: string[];
    tone?: AITone;
    aiMemory?: string;
  }
): AIResponse {
  const { intent, confidence } = classifyIntent(message);
  const { sentiment, score: sentimentScore } = detectSentiment(message);
  const language = detectLanguage(message);
  const escalation = shouldEscalate(message, settings);
  const tone: AITone = context?.tone || 'friendly';

  // Lead scoring
  const leadResult = calculateLeadScore(message, intent, sentiment, context?.contactData);

  // Determine if order related
  const orderRelated = intent === 'order_tracking' || intent === 'payment_inquiry';

  // Generate reply based on language and tone
  let reply: string;

  if (orderRelated && context?.orderData) {
    reply = generateOrderTrackingReply(context.orderData, tone);
  } else if ((language === 'Arabic' || language === 'Darija') && ARABIC_REPLY_MAP[intent]) {
    const arabicReplies = ARABIC_REPLY_MAP[intent];
    reply = arabicReplies[Math.floor(Math.random() * arabicReplies.length)];
  } else {
    const toneReplies = REPLY_MAP[intent]?.[tone] || REPLY_MAP.unknown[tone];
    reply = toneReplies[Math.floor(Math.random() * toneReplies.length)];
  }

  // Contextual enrichment
  if (context?.contactData?.fullName && !reply.includes(context.contactData.fullName)) {
    // Occasionally personalize
    if (Math.random() > 0.5 && intent !== 'greeting') {
      reply = reply.replace(/^(Hi|Hello|Hey)!?/, `Hi ${context.contactData.fullName}!`);
    }
  }

  const suggestedReplies = generateSuggestedReplies(intent, tone);

  return {
    reply,
    intent,
    confidence,
    sentiment,
    sentimentScore,
    language,
    shouldEscalate: escalation.escalate,
    escalationReason: escalation.reason,
    leadScore: leadResult.score,
    leadCategory: leadResult.category,
    suggestedReplies,
    detectedTone: tone,
    orderRelated,
  };
}

// ===================== CONVERSATION SUMMARY =====================

export function generateConversationSummary(messages: { direction: string; body: string }[]): string {
  if (messages.length === 0) return 'No messages yet.';
  const lastMessages = messages.slice(-10);
  const intents = lastMessages
    .filter(m => m.direction === 'inbound')
    .map(m => classifyIntent(m.body).intent);

  const uniqueIntents = [...new Set(intents)];
  const topics = uniqueIntents.map(i => i.replace(/_/g, ' ')).join(', ');
  const msgCount = messages.length;
  const inbound = messages.filter(m => m.direction === 'inbound').length;
  const outbound = messages.filter(m => m.direction === 'outbound').length;

  return `${msgCount} messages (${inbound} inbound, ${outbound} outbound). Topics discussed: ${topics || 'general conversation'}.`;
}

// ===================== FOLLOW-UP SUGGESTIONS =====================

export function generateFollowUpSuggestion(
  lastIntent: string,
  hoursSinceLastMessage: number,
  contactData?: any
): string | null {
  if (hoursSinceLastMessage < 2) return null;

  const followUps: Record<string, string> = {
    purchase_intent: 'Hi! Just checking — were you still interested in placing that order? I can help you finalize it! 🛍️',
    pricing_inquiry: 'Hello! I wanted to follow up on the pricing info. Would you like me to share any current promotions?',
    product_inquiry: 'Hi again! Just checking if you found what you were looking for. We have new stock in!',
    cart_abandoned: 'Hey! You left some items in your cart. Would you like to complete your order? We can hold them for you!',
  };

  if (hoursSinceLastMessage > 24 && lastIntent === 'purchase_intent') {
    return followUps.cart_abandoned || followUps.purchase_intent;
  }

  return followUps[lastIntent] || null;
}
